import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken, setToken, logoutUser } from '../utils/auth';

const BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:3030';

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: 'include',
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// `allowAnonymous` marks an optional-auth (public) read. When a refresh fails
// for such a request we show the anonymous view instead of redirecting to login.
type ReauthExtraOptions = { allowAnonymous?: boolean };

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ReauthExtraOptions
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const errorData = result.error.data as { code?: string } | undefined;
    if (errorData?.code === 'TOKEN_EXPIRED') {
      const refreshResult = await baseQuery(
        { url: '/auth/refresh', method: 'POST' },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        const { token } = refreshResult.data as { token: string };
        setToken(token);
        result = await baseQuery(args, api, extraOptions);
      } else {
        logoutUser();
        if (extraOptions?.allowAnonymous) {
          // Optional-auth read: the refresh session has ended (e.g. the user
          // returned after the refresh cookie expired), but the resource is
          // public. Retry without the now-cleared token so they see the
          // anonymous view rather than being bounced to /login. Protected
          // routes fall through to the redirect below.
          result = await baseQuery(args, api, extraOptions);
        } else {
          window.location.href = '/login';
        }
      }
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'RTKQ/questions',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Questions', 'User', 'UserQuestions', 'Bookmarks', 'Replies', 'Soulmates'],
  endpoints: () => ({}),
});
